import React, { createContext, useContext, useState, ReactNode } from 'react';
import DocumentKeyModal from '../components/DocumentKeyModal';
import { useAuth } from './AuthContext';

interface DocumentEncryptionContextType {
    masterKey: string | null;
    setMasterKey: (key: string | null) => void;
    isLocked: boolean;
    ensureUnlocked: () => Promise<string>;
}

const DocumentEncryptionContext = createContext<DocumentEncryptionContextType | undefined>(undefined);

export const DocumentEncryptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [masterKey, setMasterKey] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [resolvePromise, setResolvePromise] = useState<((key: string) => void) | null>(null);

    const isLocked = !masterKey;

    const ensureUnlocked = (): Promise<string> => {
        if (masterKey) return Promise.resolve(masterKey);

        setModalOpen(true);
        return new Promise((resolve) => {
            setResolvePromise(() => resolve);
        });
    };

    const handleKeySet = (key: string) => {
        setMasterKey(key);
        if (resolvePromise) {
            resolvePromise(key);
            setResolvePromise(null);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        // If we closed without setting the key, we might need to reject the promise
        // but usually ensureUnlocked is called before an action.
    };

    return (
        <DocumentEncryptionContext.Provider value={{ masterKey, setMasterKey, isLocked, ensureUnlocked }}>
            {children}
            {user && (
                <DocumentKeyModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onKeySet={handleKeySet}
                    userId={user.id}
                />
            )}
        </DocumentEncryptionContext.Provider>
    );
};

export const useDocumentEncryption = () => {
    const context = useContext(DocumentEncryptionContext);
    if (context === undefined) {
        throw new Error('useDocumentEncryption must be used within a DocumentEncryptionProvider');
    }
    return context;
};
